import React, { useState } from 'react';
import { UseFormReturn } from 'react-hook-form';
import { Box, Button, LinearProgress, Paper, Typography, Divider, TextField } from '@mui/material';
import { ArrowLeft, ArrowRight, CheckCircle2 } from 'lucide-react';
import { SaveButton } from "@/components/ui/buttons/save-button";
import { useLocation } from 'wouter';
import type { ProductFormValues } from '@/types/product-type';
import { ProductBasicInformationSection } from '@/components/products/form/product-basic-info';
import { ProductMediaFormSection } from '@/components/products/form/product-media-form-section';
import { ProductAttributesWizardSection } from '@/components/products/form/product-attributes-wizard-section';
import { ProductPricingWizardSection } from '@/components/products/form/product-pricing-wizard-section';
import { ProductSettingsFormSection } from '@/components/products/form/product-settings-form-section';
import { useProductCodeValidation } from '../form/use-product-code-validation';

interface CreateProductWizardProps {
  form: UseFormReturn<ProductFormValues>;
  onSubmit: (values: ProductFormValues) => Promise<void>;
  isSubmitting?: boolean;
}

const STEPS = [
  { id: 1, label: 'Basic\nInformation', fields: ['code', 'name', 'description', 'imageUrl'] as const },
  { id: 2, label: 'Attributes', fields: ['colorValues', 'sizeValues'] as const },
  { id: 3, label: 'Pricing &\nInventory', fields: ['price', 'cost', 'unit'] as const },
  { id: 4, label: 'Settings', fields: ['isActive'] as const },
];

const TOTAL_STEPS = STEPS.length;

export function CreateProductWizard({ form, onSubmit, isSubmitting }: CreateProductWizardProps) {
  const [currentStep, setCurrentStep] = useState(1);
  const [, setLocation] = useLocation();

  const { control, register, handleSubmit, formState: { errors }, setValue, watch, trigger, getValues } = form;

  const progress = (currentStep / TOTAL_STEPS) * 100;
  const stepLabels = ['Basic Information', 'Attributes', 'Pricing & Inventory', 'Settings'];

  const validateCodeUniqueness = useProductCodeValidation(form);

  const handleNext = async () => {
    const step = STEPS[currentStep - 1];
    let valid = await trigger(step.fields as any);

    // Additional validation for step 1: check if code is unique before proceeding
    if (valid && currentStep === 1) {
      const currentCode = getValues('code');
      if (currentCode) {
        valid = await validateCodeUniqueness(currentCode);
      }
    }

    if (valid) setCurrentStep((s) => Math.min(s + 1, TOTAL_STEPS));
  };

  const handleJumpToStep = async (targetStepId: number) => {
    if (targetStepId === currentStep) return;
    
    // If moving forward, validate current step first
    if (targetStepId > currentStep) {
      const step = STEPS[currentStep - 1];
      let valid = await trigger(step.fields as any);
      
      if (valid && currentStep === 1) {
        const currentCode = getValues('code');
        if (currentCode) {
          valid = await validateCodeUniqueness(currentCode);
        }
      }
      
      if (!valid) return;
    }
    
    setCurrentStep(targetStepId);
  };

  const handlePrev = () => setCurrentStep((s) => Math.max(s - 1, 1));

  const handleFinalSubmit = handleSubmit(async (values) => {
    await onSubmit(values);
  });

  // Summary values for final step
  const watchedCode = watch('code');
  const watchedName = watch('name');
  const watchedColors = (watch('colorValues') ?? []) as string[];
  const watchedSizes = (watch('sizeValues') ?? []) as string[];
  const watchedPrice = watch('price');
  const watchedIsActive = watch('isActive');
  const watchedOnStoreFront = watch('onStoreFront');

  const c = Math.max(watchedColors.length, 1);
  const s = Math.max(watchedSizes.length, 1);
  const variantCount = (watchedColors.length > 0 || watchedSizes.length > 0) ? c * s : 0;

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0, minHeight: '100vh', bgcolor: '#f8fafc' }}>
      {/* Top bar */}
      <Box sx={{ px: 3, pt: 2, pb: 1 }}>
        <Button
          variant="outlined"
          size="small"
          startIcon={<ArrowLeft size={16} />}
          onClick={() => setLocation('/products')}
          sx={{ borderRadius: 2, textTransform: 'none', borderColor: '#e2e8f0', color: '#475569' }}
        >
          Back to Products
        </Button>
      </Box>

      {/* Header */}
      <Box sx={{ px: 3, pb: 2, display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
        <Box>
          <Typography variant="h5" fontWeight={700} color="#0f172a">Create Product</Typography>
          <Typography variant="body2" color="text.secondary">Add a new product to your catalog</Typography>
        </Box>
        <Box
          sx={{
            px: 2, py: 0.5, borderRadius: 2,
            bgcolor: '#eff6ff', border: '1px solid #bfdbfe',
            color: '#2563eb', fontWeight: 600, fontSize: '0.85rem',
          }}
        >
          Step {currentStep} of {TOTAL_STEPS}
        </Box>
      </Box>

      {/* Progress card */}
      <Box sx={{ px: 3, mb: 2 }}>
        <Paper elevation={0} sx={{ p: 2.5, border: '1px solid #e2e8f0', borderRadius: 3 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
            <Typography variant="body2" fontWeight={600} color="#334155">
              {stepLabels[currentStep - 1]}
            </Typography>
            <Typography variant="body2" color="text.secondary">{Math.round(progress)}% Complete</Typography>
          </Box>
          <LinearProgress
            variant="determinate"
            value={progress}
            sx={{
              height: 6, borderRadius: 3, bgcolor: '#e2e8f0', mb: 2.5,
              '& .MuiLinearProgress-bar': {
                bgcolor: currentStep === TOTAL_STEPS ? '#16a34a' : '#1d4ed8',
                borderRadius: 3,
              },
            }}
          />

          {/* Step indicators */}
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            {STEPS.map((step, index) => {
              const isCompleted = currentStep > step.id;
              const isActive = currentStep === step.id;
              const isLast = index === STEPS.length - 1;
              return (
                <React.Fragment key={step.id}>
                  <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.5 }}>
                    <Box
                      onClick={() => handleJumpToStep(step.id)}
                      sx={{
                        width: 36, height: 36, borderRadius: '50%',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        fontWeight: 700, fontSize: '0.9rem',
                        bgcolor: isCompleted ? '#16a34a' : isActive ? '#1d4ed8' : '#e2e8f0',
                        color: isCompleted || isActive ? '#fff' : '#94a3b8',
                        transition: 'all 0.3s ease',
                        boxShadow: isActive ? '0 0 0 4px rgba(29,78,216,0.12)' : 'none',
                        cursor: 'pointer',
                        '&:hover': { opacity: 0.9 }
                      }}
                    >
                      {step.id}
                    </Box>
                    <Typography
                      onClick={() => handleJumpToStep(step.id)}
                      variant="caption"
                      sx={{
                        color: isActive ? '#1d4ed8' : isCompleted ? '#16a34a' : '#94a3b8',
                        fontWeight: isActive ? 600 : 400,
                        textAlign: 'center',
                        whiteSpace: 'pre-line',
                        lineHeight: 1.2,
                        fontSize: '0.7rem',
                        cursor: 'pointer',
                        '&:hover': { opacity: 0.8 }
                      }}
                    >
                      {step.label}
                    </Typography>
                  </Box>
                  {!isLast && (
                    <Box
                      sx={{
                        flex: 1, height: 3, mx: 0.5, borderRadius: 2, mb: 3,
                        bgcolor: isCompleted ? '#16a34a' : '#e2e8f0',
                        transition: 'background-color 0.3s ease',
                      }}
                    />
                  )}
                </React.Fragment>
              );
            })}
          </Box>
        </Paper>
      </Box>

      {/* Step content */}
      <Box sx={{ px: 3, flex: 1 }}>
        {/* Step 1: Basic Information + Image */}
        {currentStep === 1 && (
          <Paper elevation={0} sx={{ p: 3, border: '1px solid #e2e8f0', borderRadius: 3 }}>
            <Typography variant="h6" fontWeight={600} mb={0.5}>Basic Information</Typography>
            <Typography variant="body2" color="text.secondary" mb={2.5}>
              Provide essential product details.
            </Typography>
            <ProductBasicInformationSection
              control={control as any}
              register={register as any}
              errors={errors}
              inline
            />
            <Divider sx={{ my: 2.5 }} />
            <Typography variant="subtitle1" fontWeight={600} mb={0.5}>Media</Typography>
            <Typography variant="body2" color="text.secondary" mb={2}>
              Add product images to showcase your product.
            </Typography>
            <ProductMediaFormSection
              control={control as any}
              register={register as any}
              errors={errors}
              setValue={setValue as any}
              watch={watch as any}
              inline
            />
          </Paper>
        )}

        {/* Step 2: Attributes */}
        {currentStep === 2 && (
          <Paper elevation={0} sx={{ p: 3, border: '1px solid #e2e8f0', borderRadius: 3 }}>
            <Typography variant="h6" fontWeight={600} mb={0.5}>Attributes</Typography>
            <Typography variant="body2" color="text.secondary" mb={2.5}>
              Select product attributes (Color and Size) that will be used for variants
            </Typography>
            <ProductAttributesWizardSection control={control as any} watch={watch as any} />
          </Paper>
        )}

        {/* Step 3: Pricing & Inventory */}
        {currentStep === 3 && (
          <Paper elevation={0} sx={{ p: 3, border: '1px solid #e2e8f0', borderRadius: 3 }}>
            <Typography variant="h6" fontWeight={600} mb={0.5}>Pricing &amp; Inventory</Typography>
            <Typography variant="body2" color="text.secondary" mb={2.5}>
              Set your product pricing and initial stock.
            </Typography>
            <ProductPricingWizardSection control={control as any} register={register as any} errors={errors} watch={watch as any} />
          </Paper>
        )}

        {/* Step 4: Settings + Summary */}
        {currentStep === 4 && (
          <>
            <Paper elevation={0} sx={{ p: 3, border: '1px solid #e2e8f0', borderRadius: 3, mb: 2 }}>
              <Typography variant="h6" fontWeight={600} mb={0.5}>Settings</Typography>
              <Typography variant="body2" color="text.secondary" mb={2.5}>
                Final product settings and channel visibility.
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                <Box
                  sx={{
                    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                    p: 2, borderRadius: 2, border: '1px solid #e2e8f0',
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <Box
                      sx={{
                        width: 36, height: 36, borderRadius: '50%', bgcolor: '#f1f5f9',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                      }}
                    >
                      <span style={{ fontSize: 16 }}>⚙️</span>
                    </Box>
                    <Box>
                      <Typography variant="body2" fontWeight={600}>Product is active</Typography>
                      <Typography variant="caption" color="text.secondary">Make product visible in your store</Typography>
                    </Box>
                  </Box>
                  <ProductSettingsFormSection control={control as any} inline fieldName="isActive" />
                </Box>

                <Box
                  sx={{
                    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                    p: 2, borderRadius: 2, border: '1px solid #e2e8f0',
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <Box
                      sx={{
                        width: 36, height: 36, borderRadius: '50%', bgcolor: '#eff6ff',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                      }}
                    >
                      <span style={{ fontSize: 16 }}>✈️</span>
                    </Box>
                    <Box>
                      <Typography variant="body2" fontWeight={600}>Display on Telegram Mini App</Typography>
                      <Typography variant="caption" color="text.secondary">Show this product on the Telegram Mini App storefront</Typography>
                    </Box>
                  </Box>
                  <ProductSettingsFormSection control={control as any} inline fieldName="onStoreFront" />
                </Box>
              </Box>
            </Paper>

            {/* Ready to Create summary */}
            <Paper
              elevation={0}
              sx={{ p: 3, border: '1px solid #bfdbfe', borderRadius: 3, bgcolor: '#f0f9ff' }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2.5 }}>
                <Box
                  sx={{
                    width: 40, height: 40, borderRadius: '50%',
                    bgcolor: '#2563eb', display: 'flex', alignItems: 'center', justifyContent: 'center',
                  }}
                >
                  <CheckCircle2 size={22} color="#fff" />
                </Box>
                <Box>
                  <Typography variant="subtitle1" fontWeight={700}>Ready to Create</Typography>
                  <Typography variant="body2" color="text.secondary">Review your product summary before creating</Typography>
                </Box>
              </Box>

              {[
                { label: 'Product Name', value: watchedName || '—' },
                { label: 'Product Code', value: watchedCode || '—', isInput: true },
                { label: 'Variants', value: variantCount > 0 ? `${variantCount} variants` : 'No variants' },
                { label: 'Colors', value: watchedColors.length > 0 ? String(watchedColors.length) : '—' },
                { label: 'Sizes', value: watchedSizes.length > 0 ? String(watchedSizes.length) : '—' },
                { label: 'Price', value: watchedPrice ? `$${Number(watchedPrice).toFixed(2)}` : '—' },
                { label: 'Status', value: watchedIsActive ? 'Active' : 'Inactive', green: watchedIsActive },
                { label: 'Telegram Mini App', value: watchedOnStoreFront ? 'Displayed' : 'Hidden', green: watchedOnStoreFront },
              ].map((row, i) => (
                <Box
                  key={row.label}
                  sx={{
                    display: 'flex', justifyContent: 'space-between',
                    py: row.isInput ? 0.75 : 1.25,
                    borderBottom: i < 6 ? '1px solid #e0effe' : 'none',
                    alignItems: row.isInput ? 'flex-start' : 'center'
                  }}
                >
                  <Typography variant="body2" color="text.secondary" sx={{ mt: row.isInput ? 1 : 0 }}>
                    {row.label}
                  </Typography>
                  {row.isInput ? (
                    <Box sx={{ width: '220px', textAlign: 'right' }}>
                      <TextField
                        size="small"
                        fullWidth
                        {...register('code')}
                        error={!!errors.code}
                        helperText={errors.code ? (errors.code.message as string) : ""}
                        sx={{
                          '& .MuiInputBase-root': {
                            fontSize: '0.875rem',
                            bgcolor: '#fff',
                            fontWeight: 600,
                            height: 36
                          },
                          '& .MuiOutlinedInput-input': {
                            textAlign: 'right'
                          },
                          '& .MuiFormHelperText-root': {
                            m: 0,
                            mt: 0.5,
                            textAlign: 'right',
                            lineHeight: 1.2
                          }
                        }}
                      />
                    </Box>
                  ) : (
                    <Typography
                      variant="body2"
                      fontWeight={600}
                      color={row.green ? '#16a34a' : '#0f172a'}
                    >
                      {row.value}
                    </Typography>
                  )}
                </Box>
              ))}
            </Paper>
          </>
        )}
      </Box>

      {/* Footer navigation */}
      <Box
        sx={{
          px: 3, py: 2.5, mt: 3,
          display: 'flex', justifyContent: 'space-between', alignItems: 'center',
          borderTop: '1px solid #e2e8f0', bgcolor: '#fff',
          position: 'sticky', bottom: 0, zIndex: 10,
        }}
      >
        <Button
          variant="contained"
          startIcon={<ArrowLeft size={16} />}
          onClick={handlePrev}
          disabled={currentStep === 1}
          sx={{
            borderRadius: 2, textTransform: 'none',
            bgcolor: '#64748b', color: '#ffffff', fontWeight: 'bold',
            '&:hover': { bgcolor: '#475569' },
            '&:disabled': { opacity: 0.5, bgcolor: '#94a3b8', color: '#ffffff' },
          }}
        >
          Previous
        </Button>

        {currentStep < TOTAL_STEPS ? (
          <Button
            variant="contained"
            endIcon={<ArrowRight size={16} />}
            onClick={handleNext}
            sx={{
              borderRadius: 2, textTransform: 'none',
              bgcolor: '#1d4ed8', color: '#ffffff', fontWeight: 'bold',
              '&:hover': { bgcolor: '#1e40af' },
              px: 3,
            }}
          >
            Next
          </Button>
        ) : (
          <SaveButton
            handleSave={handleFinalSubmit}
            disabled={isSubmitting}
            label={isSubmitting ? 'Creating...' : 'Create Product'}
          />
        )}
      </Box>
    </Box>
  );
}
